import React from 'react'
import styled from 'styled-components'
import { colors, bp, statusColors } from '../theme'

// Shared building blocks so every page renders with one consistent look.

export const Page = styled.div`
  min-height: calc(100vh - 4.75rem);
  background: ${colors.mist};
  padding: 1.75rem;

  @media (max-width: ${bp.mobile}) {
    padding: 1rem;
  }
`

export const PageHead = styled.div`
  margin-bottom: 1.5rem;

  h1 {
    margin: 0;
    color: ${colors.ink};
    font-size: 1.8rem;
  }

  p {
    margin: 0.4rem 0 0;
    color: ${colors.slate};
    line-height: 1.6;
    max-width: 48rem;
  }
`

export const Card = styled.div`
  background: ${colors.card};
  border: 1px solid ${colors.line};
  border-radius: 16px;
  padding: 1.5rem;
`

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ cols = 4 }) => cols}, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${bp.mobile}) {
    grid-template-columns: 1fr;
  }
`

const StatWrap = styled.div`
  background: ${colors.card};
  border: 1px solid ${colors.line};
  border-left: 5px solid ${colors.green};
  border-radius: 16px;
  padding: 1.4rem;

  h3 {
    margin: 0;
    color: ${colors.slate};
    font-size: 0.9rem;
    font-weight: 600;
  }
  .value {
    margin: 0.55rem 0 0.25rem;
    color: ${colors.ink};
    font-size: 1.9rem;
    font-weight: 800;
  }
  .sub {
    margin: 0;
    color: ${colors.slate};
    font-size: 0.85rem;
  }
`

export const Stat = ({ label, value, sub }) => (
  <StatWrap>
    <h3>{label}</h3>
    <div className="value">{value}</div>
    {sub && <p className="sub">{sub}</p>}
  </StatWrap>
)

export const TableWrapper = styled.div`
  overflow-x: auto;
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: ${({ min = '720px' }) => min};

  th,
  td {
    padding: 0.9rem 1rem;
    text-align: left;
    border-bottom: 1px solid ${colors.line};
  }

  th {
    background: ${colors.mist};
    color: ${colors.ink};
    font-size: 0.88rem;
    font-weight: 700;
  }

  td {
    color: ${colors.slate};
    font-size: 0.92rem;
  }

  tbody tr:hover {
    background: ${colors.greenLight}55;
  }
`

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.75rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: ${({ state }) => (statusColors[state] || statusColors.idle).bg};
  color: ${({ state }) => (statusColors[state] || statusColors.idle).fg};
`

export const StatusPill = ({ state, label }) => <Pill state={state}>{label}</Pill>

export const Button = styled.button`
  padding: ${({ small }) => (small ? '0.5rem 0.9rem' : '0.75rem 1.3rem')};
  border: none;
  border-radius: 9px;
  background: ${({ variant }) => (variant === 'ghost' ? colors.white : colors.green)};
  color: ${({ variant }) => (variant === 'ghost' ? colors.ink : colors.white)};
  border: 1.5px solid ${({ variant }) => (variant === 'ghost' ? colors.line : colors.green)};
  font-weight: 700;
  font-size: 0.92rem;
  cursor: pointer;
  transition: 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ variant }) => (variant === 'ghost' ? colors.mist : colors.greenDark)};
    border-color: ${({ variant }) => (variant === 'ghost' ? colors.green : colors.greenDark)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const Input = styled.input`
  padding: 0.7rem 0.9rem;
  border: 1px solid ${colors.line};
  border-radius: 9px;
  outline: none;
  font-size: 0.95rem;
  background: ${colors.white};
  transition: 0.2s ease;

  &:focus {
    border-color: ${colors.green};
    box-shadow: 0 0 0 3px ${colors.greenLight};
  }
`

export const Select = styled.select`
  padding: 0.7rem 0.9rem;
  border: 1px solid ${colors.line};
  border-radius: 9px;
  outline: none;
  font-size: 0.95rem;
  background: ${colors.white};
  cursor: pointer;

  &:focus {
    border-color: ${colors.green};
  }
`

// --- async view states -----------------------------------------------------
const StateBox = styled.div`
  padding: 3rem 1.5rem;
  text-align: center;
  color: ${colors.slate};

  .spinner {
    width: 34px;
    height: 34px;
    border: 3px solid ${colors.line};
    border-top-color: ${colors.green};
    border-radius: 50%;
    margin: 0 auto 1rem;
    animation: spin 0.8s linear infinite;
  }

  .title {
    color: ${colors.ink};
    font-weight: 700;
    margin-bottom: 0.35rem;
  }

  .msg {
    font-size: 0.92rem;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const Loading = ({ label = 'Loading…' }) => (
  <StateBox>
    <div className="spinner" />
    <div className="msg">{label}</div>
  </StateBox>
)

export const ErrorState = ({ message, onRetry }) => (
  <StateBox>
    <div className="title">Something went wrong</div>
    <div className="msg">{message}</div>
    {onRetry && (
      <div style={{ marginTop: '1rem' }}>
        <Button small onClick={onRetry}>
          Retry
        </Button>
      </div>
    )}
  </StateBox>
)

export const EmptyState = ({ title = 'Nothing here yet', message }) => (
  <StateBox>
    <div className="title">{title}</div>
    {message && <div className="msg">{message}</div>}
  </StateBox>
)
